"use client";

import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import cn from "classnames";
import React from "react";
import { Icon } from "../../../Icon";
import { LeadFormEncodedTemplate } from "../../components/LeadFormEncodedTemplate";
import { LeadFormFootnote } from "../../components/LeadFormFootnote";
import { LeadFormPluginScript } from "../../components/LeadFormPluginScript";
import { LeadFormIntegratedProps } from "../../LeadFormIntegrated.types";
import { useLeadFormIntegrated } from "../../useLeadFormIntegrated";
// @ts-ignore
import baseStyles from "../Default/Default.module.scss";
// @ts-ignore
import styles from "./Stepper.module.scss";

interface StepperCSSProperties extends React.CSSProperties {
  "--stepper-active-heading-color"?: string;
  "--stepper-active-bg-color"?: string;
  "--stepper-inactive-heading-text-color"?: string;
  "--stepper-inactive-heading-icon-color"?: string;
  "--stepper-inactive-bg-color"?: string;
}

const DEFAULT_ACTIVE_HEADING_COLOR = "--text-neutral-inverse-light";
const DEFAULT_ACTIVE_BACKGROUND_COLOR = "--surface-neutral-black";

export const Stepper: React.FC<LeadFormIntegratedProps> = ({
  className,
  loadedClassName = "",
  packageId = "",
  enableStyles = true,
  enableMultiColumn = false,
  leadFormType = "TY",
  footnote,
  isLoading,
  thankYouURL,
  showFormHeader = false,
  isDrawerOpen = false,
  title,
  subtitle,
  applyDomain = "",
  phoneNumber,
  parseMetadata,
  location,
  formType,
  variation: _variation,
  stepper_label_content_entries,
  stepper_active_heading_color,
  stepper_active_background_color,
  stepper_inactive_heading_text_color,
  stepper_inactive_heading_icon_color,
  onStepChange,
  ...rest
}) => {
  const {
    formContainerRef,
    isLastStep,
    loaded,
    setLoaded,
    formId,
    salt,
    finalTitle,
    finalSubtitle,
    stepNumber,
    numberOfSteps,
    getMountClassName,
  } = useLeadFormIntegrated({
    packageId,
    applyDomain,
    leadFormType,
    thankYouURL,
    isDrawerOpen,
    title,
    subtitle,
    location,
    onStepChange,
  });

  const stepLabels = (stepper_label_content_entries ?? []).map((entry) => {
    const subhead = entry?.fields?.subhead;
    return subhead ? documentToReactComponents(subhead) : null;
  });

  const stepperStyle: StepperCSSProperties = {
    "--stepper-active-heading-color": `var(${stepper_active_heading_color ?? DEFAULT_ACTIVE_HEADING_COLOR})`,
    "--stepper-active-bg-color": `var(${stepper_active_background_color ?? DEFAULT_ACTIVE_BACKGROUND_COLOR})`,
    ...(stepper_inactive_heading_text_color && {
      "--stepper-inactive-heading-text-color": `var(${stepper_inactive_heading_text_color})`,
    }),
    ...(stepper_inactive_heading_icon_color && {
      "--stepper-inactive-heading-icon-color": `var(${stepper_inactive_heading_icon_color})`,
    }),
  };

  return (
    <div
      className={cn(className, {
        [styles.hasActiveBg]: stepNumber > 1,
      })}
      style={stepperStyle}
      ref={formContainerRef}
    >
      <LeadFormEncodedTemplate
        salt={salt}
        finalTitle={finalTitle}
        finalSubtitle={finalSubtitle}
        showFormHeader={showFormHeader}
      />
      {!isLoading && (
        <>
          <header className={styles.header}>
            {Array.from({ length: Math.max(numberOfSteps, stepNumber) }, (_, i) => {
              const step = i + 1;
              const isActive = step === stepNumber;
              return stepNumber >= step ? (
                <div key={step} className={cn(styles.headerText, { [styles.active]: isActive })}>
                  {stepLabels[step - 1] ?? `Step ${step}`}
                  {!isActive && <Icon name="check" size="lg" className={styles.checkIcon} />}
                </div>
              ) : null;
            })}
          </header>
          <div
            {...rest}
            id={formId}
            className={getMountClassName({
              styles: baseStyles,
              loadedClassName,
              loaded,
              enableStyles,
              enableMultiColumn,
              leadFormType,
              isLastStep,
              extraClasses: { [styles.stepperLeadForm]: true },
            })}
            data-form-type={formType}
          />
          <footer className={styles.footer}>
            {Array.from({ length: Math.max(numberOfSteps - 1, 0) }, (_, i) => {
              const step = i + 2;
              return stepNumber < step ? (
                <div key={step} className={styles.footerText}>
                  {stepLabels[step - 1] ?? `Step ${step}`}
                </div>
              ) : null;
            })}
          </footer>
          {footnote && (
            <LeadFormFootnote
              footnote={footnote}
              parseMetadata={parseMetadata}
              phoneNumber={phoneNumber}
              isLastStep={!!isLastStep}
              styles={baseStyles}
            />
          )}
          <LeadFormPluginScript salt={salt} onLoad={() => setLoaded(true)} />
        </>
      )}
    </div>
  );
};
